import { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * PDF-Anzeige für ERP **und** Partnerportal (ADR 0075). Zeichnet die Seiten mit
 * pdf.js selbst auf `<canvas>`, statt sie in ein `<iframe>` zu hängen.
 *
 * Liegt bewusst hier im Design-System und nicht in einer App: Beide Oberflächen
 * zeigen dieselben Belege, und die zwei Fallstricke unten würde man sich in einer
 * Kopie garantiert wieder einfangen.
 *
 * Zwei Dinge sind hier bewusst so und dürfen nicht „vereinfacht" werden:
 *
 * 1. **Kein `<iframe>`.** Safari (macOS/iOS) zeigt PDFs aus einer `blob:`-URL im
 *    Rahmen grundsätzlich nicht an — die Vorschau blieb dort leer.
 * 2. **Keine URL an pdf.js, sondern die Bytes.** Bekäme pdf.js eine `blob:`-URL,
 *    müsste es sie selbst laden: das scheitert in Safari aus dem Worker heraus und
 *    wird in Produktion zusätzlich vom CSP abgewiesen (`connect-src 'self'` deckt
 *    `blob:` nicht ab) — sichtbar als „Unexpected server response (0)".
 *    Deshalb holt diese Komponente die Bytes über `loadBytes` (Bearer-Fetch der
 *    jeweiligen App) und übergibt sie als `data`.
 */
export interface PdfViewerProps {
  /** API-Pfad des PDFs, z. B. `/v1/sales-documents/:id/pdf`. Zugleich Cache-Schlüssel. */
  path: string;
  /**
   * Holt die Bytes zum Pfad — mit der Authentifizierung der jeweiligen App
   * (ERP: Bearer-Token des Mitarbeiters, Portal: Token des Partner-Kontos).
   * Muss stabil sein; neu geladen wird nur, wenn sich `path` ändert.
   */
  loadBytes: (path: string) => Promise<Uint8Array>;
  /** Zugängliche Bezeichnung, z. B. „Angebot AN-2610042". */
  title: string;
  /** Fehlertext-Aufbereitung der App (z. B. `describeError`). */
  formatError?: (error: unknown) => string;
  className?: string;
}

/** Abstand zwischen zwei Seiten in px (auch als Trenner sichtbar). */
const PAGE_GAP = 16;
/**
 * Breite wird auf dieses Raster gerundet. Ohne das würde jeder Pixel beim Ziehen
 * am Fensterrand ein komplettes Neuzeichnen aller Seiten auslösen.
 */
const WIDTH_STEP = 8;
/** Obergrenze für die Zeichenauflösung — 2× reicht, 3× kostet nur Speicher. */
const MAX_PIXEL_RATIO = 2;

function defaultFormatError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler.';
}

export function PdfViewer({ path, loadBytes, title, formatError, className }: PdfViewerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  /** Einmal geholte Bytes — eine Breitenänderung soll nicht neu herunterladen. */
  const bytesRef = useRef<{ path: string; bytes: Uint8Array } | null>(null);
  /**
   * Lader und Fehler-Formatierer über Refs: Aufrufer geben sie oft als Pfeil-
   * funktion mit, die bei jedem Rendern neu entsteht — als Effekt-Abhängigkeit
   * würde das die Seiten endlos neu zeichnen.
   */
  const loadRef = useRef(loadBytes);
  loadRef.current = loadBytes;
  const formatRef = useRef(formatError);
  formatRef.current = formatError;
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Breite des Anzeigebereichs — Auslöser für das Neuzeichnen (fit-to-width). */
  const [width, setWidth] = useState(0);

  // Breite beobachten: bei Größenänderung (Fenster, Modal) neu zeichnen — auf
  // WIDTH_STEP gerastert, damit nicht jeder Pixel ein Neuzeichnen auslöst.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const quantize = (value: number) => Math.floor(value / WIDTH_STEP) * WIDTH_STEP;
    setWidth(quantize(host.clientWidth));
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = quantize(entries[0]?.contentRect.width ?? 0);
      setWidth((current) => (next > 0 && next !== current ? next : current));
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!width) return;
    const host = hostRef.current;
    if (!host) return;

    let isCancelled = false;
    // Aufräumen muss auch das pdf.js-Dokument treffen (eigener Worker-Thread),
    // sonst bleibt je Vorschau ein Worker am Leben.
    let cleanup: (() => void) | null = null;

    (async () => {
      try {
        const cached = bytesRef.current;
        const bytes = cached?.path === path ? cached.bytes : await loadRef.current(path);
        if (isCancelled) return;
        bytesRef.current = { path, bytes };

        // Erst hier laden: pdfjs landet in einem eigenen Chunk und fehlt im
        // Haupt-Bundle, solange niemand eine Vorschau öffnet (ADR 0075).
        const pdfjs = await import('pdfjs-dist');
        // `?url` statt `new URL(…, import.meta.url)`: Vite löst in `new URL` KEINE
        // Paket-Namen auf, nur relative Pfade — der Worker landet so als eigenes
        // Asset gleicher Herkunft (CSP `worker-src 'self'`).
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

        const task = pdfjs.getDocument({
          // KOPIE: pdf.js übergibt den Puffer an den Worker und leert ihn dabei —
          // ohne Kopie wäre `bytesRef` nach dem ersten Zeichnen unbrauchbar.
          data: bytes.slice(),
          // Das CSP erlaubt kein `unsafe-eval` — pdf.js darf also nicht evaluieren.
          isEvalSupported: false,
        });
        const document = await task.promise;
        if (isCancelled) {
          void document.destroy();
          return;
        }
        cleanup = () => void document.destroy();
        setPageCount(document.numPages);
        setError(null);

        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
        host.replaceChildren();

        for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
          const page = await document.getPage(pageNumber);
          if (isCancelled) return;
          const unscaled = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: width / unscaled.width });

          const canvas = window.document.createElement('canvas');
          canvas.width = Math.floor(viewport.width * ratio);
          canvas.height = Math.floor(viewport.height * ratio);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.className = 'bg-white shadow-sm';
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', `${title} — Seite ${pageNumber} von ${document.numPages}`);
          if (pageNumber > 1) canvas.style.marginTop = `${PAGE_GAP}px`;
          host.append(canvas);

          // jsdom (Tests) hat keinen 2D-Kontext — dann nur die Fläche anlegen,
          // statt zu werfen. Im Browser ist der Kontext immer vorhanden.
          const context = canvas.getContext('2d');
          if (!context) continue;
          context.scale(ratio, ratio);
          await page.render({ canvasContext: context, viewport }).promise;
          if (isCancelled) return;
        }
      } catch (cause) {
        if (isCancelled) return;
        const describe = formatRef.current ?? defaultFormatError;
        setError(`PDF konnte nicht angezeigt werden: ${describe(cause)}`);
      }
    })();

    return () => {
      isCancelled = true;
      cleanup?.();
    };
  }, [path, title, width]);

  return (
    <div className={cn('h-full w-full overflow-auto bg-muted/40 p-4', className)}>
      {error ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
          <AlertCircle className="h-6 w-6 text-danger-500" strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      ) : (
        <>
          <div ref={hostRef} className="mx-auto max-w-[900px]" />
          {pageCount == null && (
            <p className="py-8 text-center text-sm text-muted-foreground">PDF wird geladen …</p>
          )}
        </>
      )}
    </div>
  );
}

export default PdfViewer;
